<template>
  <div
    class="vp-table-wrapper"
    @scroll="handleScroll"
  >
    <table class="vp-table vp-events-table">
      <thead class="vp-table-header">
        <tr>
          <th
            v-for="header in tableHeaders"
            :key="header.text"
            class="px-4 py-1"
          >
            {{ header.text }}
          </th>
        </tr>
      </thead>
      <tbody class="vp-table-body">
        <VPEventsTableRow
          v-for="(event, rowIndex) in eventsStore.events"
          :key="event.id"
          :headers="tableHeaders"
          :values="getRowValues(event)"
          :index="rowIndex"
        >
          <template #totalSales="{ value }">
            <div class="flex items-center">
              <VPDonutChart :percent="eventPercentages[event.id].totalSalePercentage" />
              <span class="ml-2">{{ formatCurrency(value) }}</span>
            </div>
          </template>
          <template #totalSold="{ value }">
            <div class="flex items-center">
              <VPDonutChart :percent="eventPercentages[event.id].totalTicketsSoldPercentage" />
              <span class="ml-2">{{ value }}</span>
            </div>
          </template>
        </VPEventsTableRow>
        <tr v-if="eventLoading">
          <td :colspan="tableHeaders.length">
            <VPLoadingSpinner
              :loading="eventLoading"
              :top-message="$gettext('Loading events...')"
            />
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import { onMounted, computed } from 'vue';
import { DateTime } from 'luxon';
import { useEventStore } from '@/stores/event';
import VPDonutChart from '@/components/form/VPDonutChart.vue';
import { VPLoadingSpinner } from '@vp/vue-components';
import VPEventsTableRow from '@/components/events-table/VPEventsTableRow.vue';
import { formatCurrency } from '@/helpers/currency';
import { calculatePercentage } from '@/helpers/percentageUtils';
import { trimString } from '@/helpers/strings';
import { useGettext } from 'vue3-gettext';

const { $gettext } = useGettext();

const eventsStore = useEventStore();
const eventLoading = computed(() => eventsStore.isEventsLoading);
const allEventsLoaded = computed(() => eventsStore.allEventsLoaded);

onMounted(() => {
  loadNextPage();
});

const loadNextPage = async () => {
  await eventsStore.fetchEventsNextPage();
};

const handleScroll = async (event: Event) => {
  if (allEventsLoaded.value || eventLoading.value) {
    return;
  }

  const target = event.target as HTMLElement;

  if (
    target.scrollTop + target.clientHeight >=
    target.scrollHeight
  ) {
    loadNextPage();
  }
};

const tableHeaders = [
  { text: $gettext('Event'), key: 'event' },
  { text: $gettext('Date'), key: 'date' },
  { text: $gettext('Door / Show Time'), key: 'doorShowTime' },
  { text: $gettext('Status'), key: 'status' },
  { text: $gettext('Total Sales'), key: 'totalSales' },
  { text: $gettext('Total Sold'), key: 'totalSold' },
  { text: $gettext('Today Sales'), key: 'todaySales' },
  { text: $gettext('Today Sold'), key: 'todaySold' },
  { text: $gettext('Holds'), key: 'holds' },
  { text: $gettext('Available'), key: 'available' },
];

const getRowValues = event => [
  { value: trimString(event.name, 60), details: $gettext('Event Name') },
  {
    value: DateTime.fromISO(event.date).toLocaleString(DateTime.DATE_MED),
    details: $gettext('Event Date'),
  },
  {
    value: `${DateTime.fromISO(event.doorTime).toLocaleString(DateTime.TIME_SIMPLE).toUpperCase()} /
            ${DateTime.fromISO(event.startTime).toLocaleString(DateTime.TIME_SIMPLE).toUpperCase()}`,
    details: $gettext('Times'),
  },
  { value: event.state, details: $gettext('Event Status') },
  { value: event.ticketSales.totalTicketsSales, details: $gettext('Total Sales') },
  { value: event.ticketSales.totalTicketsSold, details: $gettext('Total Sold') },
  { value: formatCurrency(event.ticketSales.totalTicketsSalesToday), details: $gettext("Today's Sales") },
  { value: event.ticketSales.totalTicketsSoldToday, details: $gettext("Today's Sold Tickets") },
  { value: event.ticketSales.totalTicketsOnHold, details: $gettext('Tickets On Hold') },
  { value: event.ticketSales.totalTicketsAvailable, details: $gettext('Available Tickets') },
];

const eventPercentages = computed(() => {
  const percentages = {};
  eventsStore.events.forEach(event => {
    percentages[event.id] = {
      totalSalePercentage: calculatePercentage(event.ticketSales.totalTicketsSales, event.ticketSales.totalSalesLimit),
      totalTicketsSoldPercentage: calculatePercentage(event.ticketSales.totalTicketsSold, event.ticketSales.totalTickets),
    };
  });
  return percentages;
});
</script>
